import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';

import { Contact } from 'contact/contact';
import { ContactModuleColumn } from 'contact/contact-module-column';
import { ContactNote } from 'contact/contact-note';

export interface ContactNoteDocument {
	readonly creationTimestamp: number;
	readonly taskId: string;
	readonly note: string;
}

export interface ContactModuleColumnDocument {
	readonly columnId: string;
	readonly moduleId: string;
}

export interface ContactDocument {
	readonly id: string;
	readonly name: string;
	readonly ownerId: string;
	readonly language: string;
	readonly phoneNumber: string;
	readonly companies: string[];
	readonly categories: string[];
	readonly emailAddress: string;
	readonly updateTimestamp: number;
	readonly creationTimestamp: number;
	readonly notes: ContactNoteDocument[];
	readonly lastMeetingTimestamp: number;
	readonly modulesColumn: ContactModuleColumnDocument[];
}

export type ContactState = EntityState<ContactDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'contacts' })
export class ContactStore extends EntityStore<ContactState> {
	constructor() {
		super();
	}

	public asEntity(document: ContactDocument): Contact {
		if (!document) {
			return null;
		}

		return new Contact(
			document.id,
			document.name,
			document.ownerId,
			document.language,
			document.phoneNumber,
			document.companies,
			document.categories,
			document.emailAddress,
			document.updateTimestamp,
			document.creationTimestamp,
			document.lastMeetingTimestamp,
			(document.notes || []).map(
				(n) => new ContactNote(n.taskId, n.note, n.creationTimestamp)
			),
			(document.modulesColumn || []).map(
				(mc) => new ContactModuleColumn(mc.columnId, mc.moduleId)
			)
		);
	}

	public asDocument(contact: Contact): ContactDocument {
		if (!contact) {
			return null;
		}

		return {
			id: contact.id,
			name: contact.name,
			ownerId: contact.ownerId,
			language: contact.language,
			companies: contact.companies,
			categories: contact.categories,
			phoneNumber: contact.phoneNumber,
			emailAddress: contact.emailAddress,
			updateTimestamp: contact.updateTimestamp,
			creationTimestamp: contact.creationTimestamp,
			lastMeetingTimestamp: contact.lastMeetingTimestamp,
			notes: (contact.notes || []).map((n) => ({
				creationTimestamp: n.creationTimestamp,
				taskId: n.taskId,
				note: n.note,
			})),
			modulesColumn: (contact.moduleColumns || []).map((mc) => ({
				columnId: mc.columnId,
				moduleId: mc.moduleId,
			})),
		};
	}
}
